import { makeResetMutation } from '@utils/reset-store'
import Vue from 'vue'
import { cloneArray } from '@utils/Array/cloneArray/cloneArray'
import { moveMonthlyRoutineItem } from '@modules/crm/utils/monthlyRoutine'
import {
  sortCalendarDays,
  findCalendarActivitiesByPayload,
} from '@modules/crm/utils/calendar'
import { getEmailScope } from '@modules/crm/utils/emailHelpers'
import {
  getFunisFromState,
  findEtapaInFunil,
  normalizeLeadId,
  somarValorEstimadoLeads,
  isLeadFechado,
  buildResetStatusEtapaAoMover,
} from '@modules/crm/utils/leads'
import {
  forEachClienteInState,
  applyPatchToCliente,
  removeClienteFromAllClientGroups,
  upsertClienteInClientGroup,
  mergeTaskGroupsForCliente,
  mergeKanbanForCliente,
  computeSummaryFromClientGroups,
} from '@modules/crm/utils/relationship'
import {
  normalizeFunilCompleto,
  getFunisFromCompleto,
  findEtapaInFunil as findEtapaInFunilCompleto,
} from '@modules/crm/utils/oportunidadeFunil'
import { buildInitialState } from './state'

const forEachLeadInFunilSteps = (funilStepsData, cb) => {
  if (Array.isArray(funilStepsData)) {
    funilStepsData.forEach((item) => {
      if (item && Array.isArray(item.leads)) {
        item.leads.forEach(cb)
        return
      }

      const etapas = item && Array.isArray(item.etapas) ? item.etapas : []
      etapas.forEach((etapa) => {
        const leads = etapa && Array.isArray(etapa.leads) ? etapa.leads : []
        leads.forEach(cb)
      })
    })
    return
  }

  const funis =
    funilStepsData && Array.isArray(funilStepsData.funis)
      ? funilStepsData.funis
      : []

  funis.forEach((funil) => {
    const etapas = funil && Array.isArray(funil.etapas) ? funil.etapas : []

    etapas.forEach((etapa) => {
      const leads = etapa && Array.isArray(etapa.leads) ? etapa.leads : []
      leads.forEach(cb)
    })
  })
}

const forEachLeadInState = (state, idLead, cb) => {
  if (!idLead || typeof cb !== 'function') {
    return
  }

  if (state && state.leads && Array.isArray(state.leads.data)) {
    state.leads.data.forEach((lead) => {
      if (normalizeLeadId(lead) === idLead) {
        cb(lead)
      }
    })
  }

  if (state && state.funilSteps) {
    forEachLeadInFunilSteps(state.funilSteps.data, (lead) => {
      if (normalizeLeadId(lead) === idLead) {
        cb(lead)
      }
    })
  }
}

/**
 * Recalcula otimistamente o `metricas.valor_total` de uma etapa do kanban
 * a partir da soma do `valor_estimado` dos seus leads. Garante reatividade
 * via Vue.set caso a etapa ainda não possua o objeto `metricas`. Mantém o
 * header do funil sincronizado ao editar/mover/adicionar leads sem refazer
 * a request do backend.
 */
const recalcularValorTotalEtapa = (etapa) => {
  if (!etapa || !Array.isArray(etapa.leads)) return

  if (!etapa.metricas || typeof etapa.metricas !== 'object') {
    Vue.set(etapa, 'metricas', {})
  }

  Vue.set(etapa.metricas, 'valor_total', somarValorEstimadoLeads(etapa.leads))
}

const recalcularValorTotalEtapas = (...etapas) => {
  etapas.forEach((etapa) => recalcularValorTotalEtapa(etapa))
}

export const SET_LEADS = (state, payload) => {
  if (state && state.leads) {
    state.leads.data = cloneArray(payload)
    state.leads.lastUpdate = new Date()
  }
}
export const ADD_LEADS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.leads.data.find((item) => item.id === data.id)
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.leads.data.push(data)
      }
    })
  }
}
export const SET_LEADS_LOADING = (state, payload) => {
  if (state && state.leads) {
    state.leads.loading = Boolean(payload)
  }
}
export const INCREMENT_LEAD_COMMENTS_COUNT = (state, payload) => {
  const idLead = payload && payload.idLead
  forEachLeadInState(state, idLead, (lead) => {
    const current = Number(lead && lead.quantidade_comentarios)
    const base = isNaN(current) ? 0 : current
    lead.quantidade_comentarios = base + 1
  })
}

export const SET_LEAD_TASK_DONE = (state, payload) => {
  const idLead = payload && payload.idLead
  const taskId = payload && payload.taskId

  if (!taskId) {
    return
  }

  forEachLeadInState(state, idLead, (lead) => {
    const etapa = lead && lead.etapa
    const tasks =
      etapa && Array.isArray(etapa.proximas_tarefas)
        ? etapa.proximas_tarefas
        : []

    tasks.forEach((task) => {
      if (task && task.id_etapa_atividade === taskId) {
        task.feita = true
      }
    })
  })
}

export const SET_LEAD_TASK_UNDONE = (state, payload) => {
  const idLead = payload && payload.idLead
  const taskId = payload && payload.taskId

  if (!taskId) {
    return
  }

  forEachLeadInState(state, idLead, (lead) => {
    const etapa = lead && lead.etapa
    const tasks =
      etapa && Array.isArray(etapa.proximas_tarefas)
        ? etapa.proximas_tarefas
        : []

    tasks.forEach((task) => {
      if (task && task.id_etapa_atividade === taskId) {
        task.feita = false
      }
    })
  })
}

export const SET_LEADS_TAGS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.leads.tags = payload
  }
}

// Insere um lead numa etapa (guarda de duplicata + recálculo de
// valor_total). Compartilhado por ADD_LEAD_TO_KANBAN e reabrirLeadNoKanban.
const inserirLeadNaEtapa = (etapa, lead) => {
  if (!etapa || !lead) return

  if (!Array.isArray(etapa.leads)) {
    Vue.set(etapa, 'leads', [])
  }

  const exists = etapa.leads.some(
    (l) => normalizeLeadId(l) === normalizeLeadId(lead)
  )
  if (exists) return

  etapa.leads.push(lead)
  recalcularValorTotalEtapa(etapa)
}

export const ADD_LEAD_TO_KANBAN = (state, payload) => {
  const lead = payload && payload.lead
  const idFunil = payload && payload.idFunil
  const idEtapa = payload && payload.idEtapa
  if (!lead || !idFunil || !idEtapa) return

  const funis = getFunisFromState(state)
  const funil = funis.find((f) => f && f.id === idFunil)
  const etapa = findEtapaInFunil(funil, idEtapa)
  if (!etapa) return

  inserirLeadNaEtapa(etapa, structuredClone(lead))
}

export const PATCH_LEAD_IN_KANBAN = (state, payload) => {
  const idLead = payload && payload.idLead
  const patch = payload && payload.patch
  if (!idLead || !patch || typeof patch !== 'object') return

  const applyPatch = (card) => {
    Object.keys(patch).forEach((key) => {
      Vue.set(card, key, patch[key])
    })
  }

  const funis = getFunisFromState(state)
  funis.forEach((funil) => {
    if (!funil) return

    if (Array.isArray(funil.etapas)) {
      funil.etapas.forEach((etapa) => {
        if (!etapa || !Array.isArray(etapa.leads)) return
        const card = etapa.leads.find((l) => normalizeLeadId(l) === idLead)
        if (!card) return
        applyPatch(card)
        recalcularValorTotalEtapa(etapa)
      })
    }

    // Lead já ganho/perdido (fora da etapa, em funil.leadsFechados) também
    // recebe a edição — sem isso, editar descrição/tags/valor enquanto o
    // lead está fechado se perde quando ele volta a ser lead.
    if (Array.isArray(funil.leadsFechados)) {
      const card = funil.leadsFechados.find(
        (l) => normalizeLeadId(l) === idLead
      )
      if (card) applyPatch(card)
    }
  })
}

// Fecha o lead (ganho/perdido): tira de etapa.leads e empilha em
// funil.leadsFechados com o status atualizado, preservando a etapa de
// origem no próprio card (etapa.id / id_funil_vendas_etapa_atual) pra
// poder devolvê-lo à mesma coluna se o usuário reverter pra "Lead".
// Espelha fecharOportunidadeNoFunilCompleto. Se o card já estiver em
// leadsFechados (re-marcação ganho -> perda), só troca o status no lugar.
const fecharLeadNoKanban = (funis, idLead, status) => {
  funis.forEach((funil) => {
    if (!funil) return

    if (Array.isArray(funil.leadsFechados)) {
      const fechado = funil.leadsFechados.find(
        (l) => normalizeLeadId(l) === idLead
      )
      if (fechado) {
        Vue.set(fechado, 'status', status)
        return
      }
    }

    const etapas = Array.isArray(funil.etapas) ? funil.etapas : []
    etapas.forEach((etapa) => {
      if (!etapa || !Array.isArray(etapa.leads)) return
      const idx = etapa.leads.findIndex((l) => normalizeLeadId(l) === idLead)
      if (idx === -1) return
      const [card] = etapa.leads.splice(idx, 1)
      Vue.set(card, 'status', status)
      if (!Array.isArray(funil.leadsFechados)) {
        Vue.set(funil, 'leadsFechados', [])
      }
      funil.leadsFechados.push(card)
      recalcularValorTotalEtapa(etapa)
    })
  })
}

// Reabre o lead (volta a "em_progresso"): tira de funil.leadsFechados,
// atualiza o status e devolve o card à etapa de origem (card.etapa.id ou
// card.id_funil_vendas_etapa_atual). Sem o card no bucket (ex.: página
// recarregada e o lead reaberto por outra tela sem passar pelo kanban),
// usa `leadFallback` — se também não tiver etapa conhecida, é no-op
// defensivo (o kanban carrega fresco no próximo `created`).
const reabrirLeadNoKanban = (funis, idLead, status, leadFallback) => {
  let reaberto = false

  funis.forEach((funil) => {
    if (!funil || !Array.isArray(funil.leadsFechados)) return
    const idx = funil.leadsFechados.findIndex(
      (l) => normalizeLeadId(l) === idLead
    )
    if (idx === -1) return
    const [card] = funil.leadsFechados.splice(idx, 1)
    Vue.set(card, 'status', status)
    const idEtapa =
      (card.etapa && card.etapa.id) || card.id_funil_vendas_etapa_atual
    const etapa = findEtapaInFunil(funil, idEtapa)
    if (etapa) inserirLeadNaEtapa(etapa, card)
    reaberto = true
  })

  if (reaberto || !leadFallback) return

  const idEtapaFallback =
    (leadFallback.etapa && leadFallback.etapa.id) ||
    leadFallback.id_funil_vendas_etapa_atual
  if (!idEtapaFallback) return

  let inserted = false
  funis.forEach((funil) => {
    if (inserted) return
    const etapa = findEtapaInFunil(funil, idEtapaFallback)
    if (!etapa) return
    const clone = structuredClone(leadFallback)
    clone.status = status
    inserirLeadNaEtapa(etapa, clone)
    inserted = true
  })
}

// Roteia a troca de status do lead no kanban de etapas do funil (pipeline):
// ganho/perda tiram o card da etapa (fecharLeadNoKanban); voltar a
// "em_progresso" devolve (reabrirLeadNoKanban). Sem isso o card fica
// patchado com o novo status mas continua visível na coluna até o F5 —
// o endpoint do kanban não devolve leads ganhos/perdidos. Espelha
// SET_OPORTUNIDADE_DESFECHO_IN_KANBAN.
export const SET_LEAD_STATUS_IN_KANBAN = (state, payload) => {
  const idLead = payload && payload.idLead
  const status = payload && payload.status
  if (!idLead || !status) return

  const funis = getFunisFromState(state)

  if (isLeadFechado(status)) {
    fecharLeadNoKanban(funis, idLead, status)
    return
  }

  reabrirLeadNoKanban(funis, idLead, status, payload.lead)
}

export const MOVE_LEAD_IN_KANBAN = (state, payload) => {
  const idLead = payload && payload.idLead
  const fromEtapa = payload && payload.fromEtapa
  const toEtapa = payload && payload.toEtapa
  if (!idLead || !fromEtapa || !toEtapa) return

  const funis = getFunisFromState(state)
  funis.forEach((funil) => {
    if (!funil || !Array.isArray(funil.etapas)) return
    const from = findEtapaInFunil(funil, fromEtapa)
    const to = findEtapaInFunil(funil, toEtapa)
    if (
      !from ||
      !to ||
      !Array.isArray(from.leads) ||
      !Array.isArray(to.leads)
    ) {
      return
    }
    const idx = from.leads.findIndex((l) => normalizeLeadId(l) === idLead)
    if (idx === -1) return
    const [card] = from.leads.splice(idx, 1)
    if (card.etapa && typeof card.etapa === 'object') {
      Vue.set(card, 'etapa', { ...card.etapa, id: toEtapa })
    }
    Vue.set(card, 'id_funil_vendas_etapa_atual', toEtapa)
    Vue.set(card, 'status_etapa', buildResetStatusEtapaAoMover(to))
    to.leads.push(card)
  })

  funis.forEach((funil) => {
    recalcularValorTotalEtapas(
      findEtapaInFunil(funil, fromEtapa),
      findEtapaInFunil(funil, toEtapa)
    )
  })
}

export const SET_MONTHLY_ROUTINE = (state, payload) => {
  if (state && state.monthlyRoutine) {
    state.monthlyRoutine.data = cloneArray(payload)
    state.monthlyRoutine.lastUpdate = new Date()
  }
}
export const ADD_MONTHLY_ROUTINE = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      if (moveMonthlyRoutineItem(state.monthlyRoutine.data, data)) return

      let old = state.monthlyRoutine.data.find((item) => item.id === data.id)
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.monthlyRoutine.data.push(data)
      }
    })
  }
}
export const SET_MONTHLY_ROUTINE_LOADING = (state, payload) => {
  if (state && state.monthlyRoutine) {
    state.monthlyRoutine.loading = Boolean(payload)
  }
}

export const SET_FILTER_LEADS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.filterLeads = payload
  }
}

export const SET_LIGACOES = (state, payload) => {
  if (state && state.ligacoes) {
    state.ligacoes.data = cloneArray(payload)
    state.ligacoes.lastUpdate = new Date()
  }
}
export const ADD_LIGACOES = (state, payload) => {
  if (payload && typeof payload === 'object' && state && state.ligacoes) {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      if (!data || data.id === undefined || data.id === null) return
      let old = state.ligacoes.data.find((item) => item.id === data.id)
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.ligacoes.data.push(data)
      }
    })
  }
}
export const SET_LIGACOES_LOADING = (state, payload) => {
  if (state && state.ligacoes) {
    state.ligacoes.loading = Boolean(payload)
  }
}
export const SET_FILTER_LIGACOES = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.filterLigacoes = payload
  }
}

export const SET_SELECTED_FUNIL_ID = (state, payload) => {
  state.selectedFunilId = payload || null
}

export const SET_REGUA_RELACIONAMENTO = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.reguaRelacionamento.data = payload
    state.reguaRelacionamento.lastUpdate = new Date()
  }
}
export const ADD_REGUA_RELACIONAMENTO = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.reguaRelacionamento.data.find(
        (item) => item.id === data.id
      )
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.reguaRelacionamento.data.push(data)
      }
    })
  }
}
export const SET_REGUA_RELACIONAMENTO_LOADING = (state, payload) => {
  state.reguaRelacionamento.loading = payload
}

export const SET_ATIVIDADES_REGUA = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.atividadesRegua.data = payload
    state.atividadesRegua.lastUpdate = new Date()
  }
}
export const ADD_ATIVIDADES_REGUA = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.atividadesRegua.data.find((item) => item.id === data.id)
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.atividadesRegua.data.push(data)
      }
    })
  }
}
export const SET_ATIVIDADES_REGUA_LOADING = (state, payload) => {
  state.atividadesRegua.loading = payload
}
export const REMOVE_ATIVIDADES_REGUA = (state, payload) => {
  if (!payload) return
  state.atividadesRegua.data = state.atividadesRegua.data.filter(
    (item) => item.id !== payload
  )
}

export const SET_FUNIL = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.funil.data = payload
    state.funil.lastUpdate = new Date()
  }
}
export const ADD_FUNIL = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.funil.data.find((item) => item.id === data.id)
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.funil.data.push(data)
      }
    })
  }
}
export const SET_FUNIL_LOADING = (state, payload) => {
  state.funil.loading = payload
}
export const SET_FUNIL_CUSTOMIZE_LOADING = (state, payload) => {
  state.funil.customizeLoading = payload
}

export const SET_FUNIL_STEPS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.funilSteps.data = payload
    state.funilSteps.lastUpdate = new Date()
  }
}
export const ADD_FUNIL_STEPS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.funilSteps.data.find((item) => item.id === data.id)
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.funilSteps.data.push(data)
      }
    })
  }
}
export const SET_FUNIL_STEPS_LOADING = (state, payload) => {
  state.funilSteps.loading = payload
}

export const SET_FUNIL_STEPS_TASKS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.funilStepsTasks.data = payload
    state.funilStepsTasks.lastUpdate = new Date()
  }
}
export const ADD_FUNIL_STEPS_TASKS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.funilStepsTasks.data.find((item) => item.id === data.id)
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.funilStepsTasks.data.push(data)
      }
    })
  }
}
export const SET_FUNIL_STEPS_TASKS_LOADING = (state, payload) => {
  state.funilStepsTasks.loading = payload
}
export const REMOVE_FUNIL_STEPS_TASKS = (state, payload) => {
  if (!payload) return
  state.funilStepsTasks.data = state.funilStepsTasks.data.filter(
    (item) => item.id !== payload
  )
}

// ----- Funil de oportunidades ----- //
export const SET_OPORTUNIDADE_FUNIL = (state, payload) => {
  state.oportunidadeFunil.data = Array.isArray(payload) ? payload : []
  state.oportunidadeFunil.lastUpdate = new Date()
}
export const ADD_OPORTUNIDADE_FUNIL = (state, payload) => {
  if (!payload || typeof payload !== 'object') return
  const dados = Array.isArray(payload) ? payload : [payload]
  dados.forEach((data) => {
    if (!data || !data.id) return
    const old = state.oportunidadeFunil.data.find((item) => item.id === data.id)
    if (old) {
      Object.keys(data).forEach((key) => {
        old[key] = data[key]
      })
    } else {
      state.oportunidadeFunil.data.push(data)
    }
  })
}
export const SET_OPORTUNIDADE_FUNIL_LOADING = (state, payload) => {
  state.oportunidadeFunil.loading = !!payload
}

export const SET_OPORTUNIDADE_FUNIL_ETAPAS = (state, payload) => {
  state.oportunidadeFunilEtapas.data = Array.isArray(payload) ? payload : []
  state.oportunidadeFunilEtapas.lastUpdate = new Date()
}
export const SET_OPORTUNIDADE_FUNIL_ETAPAS_LOADING = (state, payload) => {
  state.oportunidadeFunilEtapas.loading = !!payload
}

export const SET_OPORTUNIDADE_FUNIL_ATIVIDADES = (state, payload) => {
  state.oportunidadeFunilAtividades.data = Array.isArray(payload) ? payload : []
  state.oportunidadeFunilAtividades.lastUpdate = new Date()
}
export const SET_OPORTUNIDADE_FUNIL_ATIVIDADES_LOADING = (state, payload) => {
  state.oportunidadeFunilAtividades.loading = !!payload
}

export const SET_OPORTUNIDADE_FUNIL_COMPLETO = (state, payload) => {
  state.oportunidadeFunilCompleto.data = normalizeFunilCompleto(payload)
  state.oportunidadeFunilCompleto.lastUpdate = new Date()
}
export const SET_OPORTUNIDADE_FUNIL_COMPLETO_LOADING = (state, payload) => {
  state.oportunidadeFunilCompleto.loading = !!payload
}

// Move otimista de uma oportunidade entre etapas do funil completo
// (oportunidadeFunilCompleto.data.funis[].etapas[].oportunidades[]). Espelha
// MOVE_LEAD_IN_KANBAN: move o card de fromEtapa para toEtapa (match por id da
// oportunidade), re-etiqueta card.id_etapa com toEtapa via Vue.set. O caller
// (controller) é responsável pelo rollback em caso de falha da API.
export const MOVE_OPORTUNIDADE_IN_KANBAN = (state, payload) => {
  const idOportunidade = payload && payload.idOportunidade
  const fromEtapa = payload && payload.fromEtapa
  const toEtapa = payload && payload.toEtapa
  if (!idOportunidade || !fromEtapa || !toEtapa) return

  const funis = getFunisFromCompleto(
    state && state.oportunidadeFunilCompleto
      ? state.oportunidadeFunilCompleto.data
      : null
  )
  funis.forEach((funil) => {
    if (!funil || !Array.isArray(funil.etapas)) return
    const from = findEtapaInFunilCompleto(funil, fromEtapa)
    const to = findEtapaInFunilCompleto(funil, toEtapa)
    if (
      !from ||
      !to ||
      !Array.isArray(from.oportunidades) ||
      !Array.isArray(to.oportunidades)
    ) {
      return
    }
    const idx = from.oportunidades.findIndex(
      (o) => o && o.id === idOportunidade
    )
    if (idx === -1) return
    const [card] = from.oportunidades.splice(idx, 1)
    Vue.set(card, 'id_etapa', toEtapa)
    to.oportunidades.push(card)
  })
}

// Mapa desfecho -> status persistido no card (alinhado ao backend §3.7).
const STATUS_BY_DESFECHO = {
  ganho: 'sucesso',
  perdido: 'declinado',
  andamento: 'pendente',
}

// Fecha a oportunidade (ganho/perdido): tira de etapa.oportunidades e empilha em
// funil.fechados com o status do desfecho, mantendo id_etapa p/ a coluna correta.
// Se já estiver em fechados (re-marcação), só troca o status no lugar. Helper de
// mutação (muta o funil completo, como recalcularValorTotalEtapa).
const fecharOportunidadeNoFunilCompleto = (funis, idOportunidade, status) => {
  funis.forEach((funil) => {
    if (!funil) return

    if (Array.isArray(funil.fechados)) {
      const fechado = funil.fechados.find((o) => o && o.id === idOportunidade)
      if (fechado) {
        Vue.set(fechado, 'status', status)
        return
      }
    }

    const etapas = Array.isArray(funil.etapas) ? funil.etapas : []
    etapas.forEach((etapa) => {
      if (!etapa || !Array.isArray(etapa.oportunidades)) return
      const idx = etapa.oportunidades.findIndex(
        (o) => o && o.id === idOportunidade
      )
      if (idx === -1) return
      const [card] = etapa.oportunidades.splice(idx, 1)
      Vue.set(card, 'status', status)
      Vue.set(card, 'id_etapa', etapa.id)
      if (!Array.isArray(funil.fechados)) Vue.set(funil, 'fechados', [])
      funil.fechados.push(card)
    })
  })
}

// Reabre a oportunidade (andamento): tira de funil.fechados, status pendente,
// limpa fechamento/motivo e devolve à etapa de id_etapa. Sem etapa correspondente,
// fica fora dos buckets abertos (defensivo) mas removida de fechados.
const reabrirOportunidadeNoFunilCompleto = (funis, idOportunidade) => {
  funis.forEach((funil) => {
    if (!funil || !Array.isArray(funil.fechados)) return
    const idx = funil.fechados.findIndex((o) => o && o.id === idOportunidade)
    if (idx === -1) return
    const [card] = funil.fechados.splice(idx, 1)
    Vue.set(card, 'status', 'pendente')
    Vue.set(card, 'data_fechamento', null)
    Vue.set(card, 'id_motivo_perda', null)
    Vue.set(card, 'motivo_perda_texto', null)
    const etapa = findEtapaInFunilCompleto(funil, card.id_etapa)
    if (etapa && Array.isArray(etapa.oportunidades)) {
      etapa.oportunidades.push(card)
    }
  })
}

// Desfecho otimista da oportunidade no funil completo: ganho/perdido movem
// open -> fechados (status sucesso/declinado); andamento (reabrir) move
// fechados -> etapa.oportunidades (status pendente). Espelha
// MOVE_OPORTUNIDADE_IN_KANBAN; o controller faz a ressincronização em caso de
// falha da API. No-op quando a oportunidade não é encontrada.
export const SET_OPORTUNIDADE_DESFECHO_IN_KANBAN = (state, payload) => {
  const idOportunidade = payload && payload.idOportunidade
  const desfecho = payload && payload.desfecho
  if (!idOportunidade || !desfecho) return
  const status = STATUS_BY_DESFECHO[desfecho]
  if (!status) return

  const funis = getFunisFromCompleto(
    state && state.oportunidadeFunilCompleto
      ? state.oportunidadeFunilCompleto.data
      : null
  )

  if (status === 'pendente') {
    reabrirOportunidadeNoFunilCompleto(funis, idOportunidade)
    return
  }
  fecharOportunidadeNoFunilCompleto(funis, idOportunidade, status)
}

// Localiza uma oportunidade (por id) em qualquer etapa aberta de qualquer funil
// do funil completo. Retorna { funil, etapa, index, card } ou null. Espelha o
// estilo dos demais finders/walkers deste arquivo (forEach + guarda).
const findOportunidadeInFunis = (funis, id) => {
  let achado = null
  funis.forEach((funil) => {
    if (achado || !funil || !Array.isArray(funil.etapas)) return
    funil.etapas.forEach((etapa) => {
      if (achado || !etapa || !Array.isArray(etapa.oportunidades)) return
      const index = etapa.oportunidades.findIndex((o) => o && o.id === id)
      if (index !== -1) {
        achado = { funil, etapa, index, card: etapa.oportunidades[index] }
      }
    })
  })
  return achado
}

// Patch (merge campo a campo via Vue.set) de um card de oportunidade. Preserva o
// cliente enriquecido quando o patch não traz um cliente válido — a edição vinda
// do modal pode não reenviar o cliente completo; quando traz, faz merge (mantém
// id_lead/status_lead já presentes no card).
const patchOportunidadeCard = (card, patch) => {
  Object.keys(patch).forEach((key) => {
    if (key === 'cliente') {
      const incoming = patch.cliente
      const temIncoming =
        incoming &&
        typeof incoming === 'object' &&
        Object.keys(incoming).length > 0
      if (!temIncoming) return
      Vue.set(card, 'cliente', { ...card.cliente, ...incoming })
      return
    }
    Vue.set(card, key, patch[key])
  })
}

// Insere o card numa etapa alvo (criação). No-op se funil/etapa não resolvem.
const inserirOportunidadeNaEtapa = (funis, oportunidade, idFunil, idEtapa) => {
  const funil = funis.find((f) => f && f.id === idFunil)
  const etapa = findEtapaInFunilCompleto(funil, idEtapa)
  if (!etapa) return
  if (!Array.isArray(etapa.oportunidades)) {
    Vue.set(etapa, 'oportunidades', [])
  }
  etapa.oportunidades.push(oportunidade)
}

// Move um card já localizado (`existing`) para a etapa de destino. No-op se o
// destino não resolve ou já é a etapa atual. Espelha MOVE_OPORTUNIDADE_IN_KANBAN.
const moverOportunidadeEntreEtapas = (funis, existing, idFunil, idEtapa) => {
  const targetFunil = funis.find((f) => f && f.id === idFunil)
  const targetEtapa = findEtapaInFunilCompleto(targetFunil, idEtapa)
  if (!targetEtapa || targetEtapa === existing.etapa) return
  const [card] = existing.etapa.oportunidades.splice(existing.index, 1)
  Vue.set(card, 'id_etapa', idEtapa)
  if (!Array.isArray(targetEtapa.oportunidades)) {
    Vue.set(targetEtapa, 'oportunidades', [])
  }
  targetEtapa.oportunidades.push(card)
}

// Upsert otimista de uma oportunidade no funil completo
// (oportunidadeFunilCompleto.data.funis[].etapas[].oportunidades[]). Espelha
// ADD_LEAD_TO_KANBAN/PATCH_LEAD_IN_KANBAN: na CRIAÇÃO insere o card na etapa
// alvo; na EDIÇÃO faz patch no lugar (preservando o cliente enriquecido) e, se a
// etapa mudou, move o card para a etapa de destino. Defensivo: params/dados
// inválidos ou funil/etapa não resolvidos => no-op (um reload real reconcilia
// depois). O caller fecha o modal e só ressincroniza em caso de falha da API.
export const UPSERT_OPORTUNIDADE_IN_KANBAN = (state, payload) => {
  const oportunidade = payload && payload.oportunidade
  if (!oportunidade || !oportunidade.id) return
  const idFunil = payload && payload.idFunil
  const idEtapa = payload && payload.idEtapa
  const fromEtapa = payload && payload.fromEtapa ? payload.fromEtapa : null

  const funis = getFunisFromCompleto(
    state && state.oportunidadeFunilCompleto
      ? state.oportunidadeFunilCompleto.data
      : null
  )
  if (!funis.length) return

  const existing = findOportunidadeInFunis(funis, oportunidade.id)

  // CRIAÇÃO: não existe em lugar nenhum -> insere o card na etapa alvo.
  if (!existing) {
    inserirOportunidadeNaEtapa(funis, oportunidade, idFunil, idEtapa)
    return
  }

  // EDIÇÃO: patch no lugar (merge campo a campo, preservando o cliente).
  patchOportunidadeCard(existing.card, oportunidade)

  // Caller sinaliza que a etapa não mudou (fromEtapa == idEtapa): nada a mover.
  if (fromEtapa && String(fromEtapa) === String(idEtapa)) return

  moverOportunidadeEntreEtapas(funis, existing, idFunil, idEtapa)
}

export const SET_OPORTUNIDADE_MOTIVOS_PERDA = (state, payload) => {
  state.oportunidadeMotivosPerda.data = Array.isArray(payload) ? payload : []
  state.oportunidadeMotivosPerda.lastUpdate = new Date()
}
export const SET_OPORTUNIDADE_MOTIVOS_PERDA_LOADING = (state, payload) => {
  state.oportunidadeMotivosPerda.loading = !!payload
}

export const SET_ORIGENS = (state, payload) => {
  state.origens.data = Array.isArray(payload) ? payload : []
  state.origens.lastUpdate = new Date()
}
export const ADD_ORIGEM = (state, payload) => {
  if (!payload || typeof payload !== 'object') return
  const dados = Array.isArray(payload) ? payload : [payload]
  dados.forEach((data) => {
    if (!data || !data.id) return
    const old = state.origens.data.find((item) => item.id === data.id)
    if (old) {
      Object.keys(data).forEach((key) => {
        old[key] = data[key]
      })
    } else {
      state.origens.data.push(data)
    }
  })
}
export const SET_ORIGENS_LOADING = (state, payload) => {
  state.origens.loading = !!payload
}
export const REMOVE_ORIGEM = (state, payload) => {
  if (!payload) return
  state.origens.data = state.origens.data.filter((item) => item.id !== payload)
}

export const SET_REGUA_CLIENTE = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.reguaCliente.data = payload
    state.reguaCliente.lastUpdate = new Date()
  }
}
export const ADD_REGUA_CLIENTE = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.reguaCliente.data.find((item) => item.id === data.id)
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.reguaCliente.data.push(data)
      }
    })
  }
}
export const SET_REGUA_CLIENTE_LOADING = (state, payload) => {
  state.reguaCliente.loading = payload
}

export const SET_HISTORICO_ATIVIDADE = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.historicoAtividade.data = payload
    state.historicoAtividade.lastUpdate = new Date()
  }
}
export const ADD_HISTORICO_ATIVIDADE = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.historicoAtividade.data.find(
        (item) => item.id === data.id
      )
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.historicoAtividade.data.push(data)
      }
    })
  }
}
export const SET_HISTORICO_ATIVIDADE_LOADING = (state, payload) => {
  state.historicoAtividade.loading = payload
}

export const SET_ATIVIDADES_SUGERIDAS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.atividadesSugeridas.data = payload
    state.atividadesSugeridas.lastUpdate = new Date()
  }
}
export const ADD_ATIVIDADES_SUGERIDAS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.atividadesSugeridas.data.find(
        (item) => item.id === data.id
      )
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.atividadesSugeridas.data.push(data)
      }
    })
  }
}
export const SET_ATIVIDADES_SUGERIDAS_LOADING = (state, payload) => {
  state.atividadesSugeridas.loading = payload
}

export const SET_CLIENTS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.clients.data = payload
    state.clients.lastUpdate = new Date()
  }
}
export const SET_CLIENTS_SUMMARY = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.clients.summaryData = payload
  }
}
export const SET_CLIENTS_TAGS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.clients.tags = payload
  }
}
export const ADD_CLIENTS = (state, payload) => {
  const isRelationshipMerge = (data, key, old) => {
    return (
      key === 'relacionamento' &&
      old[key] &&
      typeof old[key] === 'object' &&
      data[key] &&
      typeof data[key] === 'object'
    )
  }
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let clienteAtualizado = false

      state.clients.data.forEach((grupo) => {
        const old = grupo.clientes.find(
          (c) => c.id_cliente_app === data.id_cliente_app
        )
        if (old) {
          Object.keys(data).forEach((key) => {
            if (isRelationshipMerge(data, key, old)) {
              old[key] = {
                ...old[key],
                ...data[key],
              }
              return
            }

            old[key] = data[key]
          })
          clienteAtualizado = true
        }
      })
      if (!clienteAtualizado) {
        const grupoDestino = state.clients.data.find(
          (g) => g.identificador === data.grupo
        )
        if (grupoDestino) {
          grupoDestino.clientes.push(data)
          grupoDestino.total = (grupoDestino.total || 0) + 1
        }
      }
    })

    // Forca troca de referencia para invalidar getters/computed que observam
    state.clients.data = [...state.clients.data]
  }
}
export const SET_CLIENTS_LOADING = (state, payload) => {
  state.clients.loading = payload
}

export const SET_CLIENTS_TASKS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.clientsTasks.data = payload
    state.clientsTasks.lastUpdate = new Date()
  }
}
export const SET_CLIENTS_TASKS_SUMMARY = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.clientsTasks.summaryData = payload
  }
}
export const SET_CLIENTS_TASKS_TAGS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.clientsTasks.tags = payload
  }
}
export const ADD_CLIENTS_TASKS = (state, payload) => {
  if (!payload || typeof payload !== 'object') return

  let dados = JSON.parse(JSON.stringify(payload))
  if (!Array.isArray(dados)) {
    dados = [dados]
  }

  dados.forEach((data) => {
    state.clientsTasks.data.forEach((grupo) => {
      if (!Array.isArray(grupo.tarefas)) return

      grupo.tarefas.forEach((task) => {
        if (!task.cliente) return
        if ((task.cliente || {}).id_cliente_app !== data.id_cliente_app) return

        Object.keys(data).forEach((k) => {
          task.cliente[k] = data[k]
        })
      })
    })
  })
}
export const SET_CLIENTS_TASKS_LOADING = (state, payload) => {
  state.clientsTasks.loading = payload
}
export const SET_CLIENTS_TASKS_LOADING_NO_SAVE = (state, payload) => {
  state.clientsTasks.loadingNoSave = payload
}

export const SET_RELATIONSHIP_FILTERS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.filtersRelationship.data = payload
  }
}
export const SET_LEAD_TIMELINE = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.leadTimeline.data = payload
    state.leadTimeline.lastUpdate = new Date()
  }
}
export const ADD_LEAD_TIMELINE = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.leadTimeline.data.find((item) => item.id === data.id)
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.leadTimeline.data.push(data)
      }
    })
  }
}
export const SET_LEAD_TIMELINE_LOADING = (state, payload) => {
  state.leadTimeline.loading = payload
}

export const SET_K_Y_C = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.kYC.data = payload
    state.kYC.lastUpdate = new Date()
  }
}
export const ADD_K_Y_C = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.kYC.data.find((item) => item.id === data.id)
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.kYC.data.push(data)
      }
    })
  }
}
export const SET_K_Y_C_LOADING = (state, payload) => {
  state.kYC.loading = payload
}
export const SET_K_Y_C_LIST = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.kYCList.data = payload
    state.kYCList.lastUpdate = new Date()
  }
}
export const SET_K_Y_C_LIST_LOADING = (state, payload) => {
  state.kYCList.loading = payload
}

export const SET_HISTORICO_LEAD = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.historicoLead.data = payload
    state.historicoLead.lastUpdate = new Date()
  }
}
export const ADD_HISTORICO_LEAD = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.historicoLead.data.find((item) => item.id === data.id)
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.historicoLead.data.push(data)
      }
    })
  }
}
export const SET_HISTORICO_LEAD_LOADING = (state, payload) => {
  state.historicoLead.loading = payload
}
export const SET_TIMELINE_CLIENT = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.timelineClient.data = payload
    state.timelineClient.lastUpdate = new Date()
  }
}
export const ADD_TIMELINE_CLIENT = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.timelineClient.data.find((item) => item.id === data.id)
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.timelineClient.data.push(data)
      }
    })
  }
}
export const SET_TIMELINE_CLIENT_LOADING = (state, payload) => {
  state.timelineClient.loading = payload
}

export const SET_EMPRESAS = (state, payload) => {
  if (state && state.empresas) {
    if (Array.isArray(payload)) {
      state.empresas.data = payload.slice()
    } else {
      state.empresas.data = []
    }
    state.empresas.lastUpdate = new Date()
  }
}
export const ADD_EMPRESAS = (state, payload) => {
  if (state && state.empresas) {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((empresa) => {
      if (!empresa || typeof empresa !== 'object') return
      const antigo = state.empresas.data.find((item) => {
        if (!item || typeof item !== 'object') {
          return false
        }
        return item.id === empresa.id
      })
      if (antigo) {
        Object.keys(empresa).forEach((key) => {
          antigo[key] = empresa[key]
        })
      } else {
        state.empresas.data.push(empresa)
      }
    })
    state.empresas.lastUpdate = new Date()
  }
}
export const SET_EMPRESAS_LOADING = (state, payload) => {
  if (state && state.empresas) {
    state.empresas.loading = Boolean(payload)
  }
}

export const SET_GRUPOS_FAMILIARES = (state, payload) => {
  if (state && state.gruposFamiliares) {
    if (Array.isArray(payload)) {
      state.gruposFamiliares.data = payload.slice()
    } else {
      state.gruposFamiliares.data = []
    }
    state.gruposFamiliares.lastUpdate = new Date()
  }
}
export const ADD_GRUPOS_FAMILIARES = (state, payload) => {
  if (state && state.gruposFamiliares) {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((grupo) => {
      if (!grupo || typeof grupo !== 'object') return
      const antigo = state.gruposFamiliares.data.find((item) => {
        if (!item || typeof item !== 'object') {
          return false
        }
        return item.id === grupo.id
      })
      if (antigo) {
        Object.keys(grupo).forEach((key) => {
          antigo[key] = grupo[key]
        })
      } else {
        state.gruposFamiliares.data.push(grupo)
      }
    })
    state.gruposFamiliares.lastUpdate = new Date()
  }
}
export const SET_GRUPOS_FAMILIARES_LOADING = (state, payload) => {
  if (state && state.gruposFamiliares) {
    state.gruposFamiliares.loading = Boolean(payload)
  }
}
export const SET_GRUPOS_FAMILIARES_LISTAGEM = (state, payload) => {
  if (state && state.gruposFamiliaresListagem) {
    if (Array.isArray(payload)) {
      state.gruposFamiliaresListagem.data = payload.slice()
    } else {
      state.gruposFamiliaresListagem.data = []
    }
    state.gruposFamiliaresListagem.lastUpdate = new Date()
  }
}
export const SET_GRUPOS_FAMILIARES_LISTAGEM_LOADING = (state, payload) => {
  if (state && state.gruposFamiliaresListagem) {
    state.gruposFamiliaresListagem.loading = Boolean(payload)
  }
}
export const SET_CRM_INTEGRATION_EMAIL = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.crmIntegrationEmail.data = payload
    state.crmIntegrationEmail.lastUpdate = new Date()
  }
}
export const ADD_CRM_INTEGRATION_EMAIL = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.crmIntegrationEmail.data.find(
        (item) => item.id === data.id
      )
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.crmIntegrationEmail.data.push(data)
      }
    })
  }
}
export const SET_CRM_INTEGRATION_EMAIL_LOADING = (state, payload) => {
  state.crmIntegrationEmail.loading = payload
}
export const SET_CRM_INTEGRATION_PROVIDERS = (state, payload) => {
  state.crmIntegrationEmail.providers = Array.isArray(payload) ? payload : []
}

export const SET_OVERVIEW_INDICATORS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.overviewIndicators.data = payload
    state.overviewIndicators.lastUpdate = new Date()
  }
}
export const ADD_OVERVIEW_INDICATORS = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.overviewIndicators.data.find(
        (item) => item.id === data.id
      )
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.overviewIndicators.data.push(data)
      }
    })
  }
}
export const SET_OVERVIEW_INDICATORS_LOADING = (state, payload) => {
  state.overviewIndicators.loading = payload
}
export const SET_FUNIL_STEPS_CONFIG = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.funilStepsConfig.data = payload
    state.funilStepsConfig.lastUpdate = new Date()
  }
}
export const ADD_FUNIL_STEPS_CONFIG = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.funilStepsConfig.data.find((item) => item.id === data.id)
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.funilStepsConfig.data.push(data)
      }
    })
  }
}
export const REMOVE_FUNIL_STEPS_CONFIG = (state, payload) => {
  if (!payload) return
  state.funilStepsConfig.data = state.funilStepsConfig.data.filter(
    (item) => item.id !== payload
  )
}
export const SET_FUNIL_STEPS_CONFIG_LOADING = (state, payload) => {
  state.funilStepsConfig.loading = payload
}

export const SET_RELATIONSHIP_KANBAN = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.relationshipKanban.data = payload
    state.relationshipKanban.lastUpdate = new Date()
  }
}
export const ADD_RELATIONSHIP_KANBAN = (state, payload) => {
  if (payload && typeof payload === 'object') {
    let dados = JSON.parse(JSON.stringify(payload))
    if (!Array.isArray(dados)) {
      dados = [dados]
    }
    dados.forEach((data) => {
      let old = state.relationshipKanban.data.find(
        (item) => item.id === data.id
      )
      if (old) {
        Object.keys(data).forEach((key) => {
          old[key] = data[key]
        })
      } else {
        state.relationshipKanban.data.push(data)
      }
    })
  }
}
export const SET_RELATIONSHIP_KANBAN_LOADING = (state, payload) => {
  state.relationshipKanban.loading = payload
}

export const SET_OPPORTUNITIES_KANBAN = (state, payload) => {
  if (state && state.opportunitiesKanban) {
    state.opportunitiesKanban.data = Array.isArray(payload)
      ? cloneArray(payload)
      : []
    state.opportunitiesKanban.lastUpdate = new Date()
  }
}
export const SET_OPPORTUNITIES_KANBAN_LOADING = (state, payload) => {
  if (state && state.opportunitiesKanban) {
    state.opportunitiesKanban.loading = Boolean(payload)
  }
}
// Adiciona ou atualiza um item da oportunidade no kanban — usado quando o
// usuario cria/edita uma oportunidade via modal e queremos refletir na UI
// instantaneamente sem rodar uma nova requisicao no endpoint de tasks.
export const ADD_OPPORTUNITIES_KANBAN = (state, payload) => {
  if (!payload || !state || !state.opportunitiesKanban) return
  const dados = Array.isArray(payload) ? payload : [payload]
  dados.forEach((item) => {
    if (!item || !item.id) return
    const idx = state.opportunitiesKanban.data.findIndex(
      (i) => i.id === item.id
    )
    if (idx >= 0) {
      state.opportunitiesKanban.data.splice(idx, 1, item)
    } else {
      state.opportunitiesKanban.data.push(item)
    }
  })
}
// Remove uma ou mais oportunidades do kanban via id. Usado para refletir a
// exclusao instantaneamente sem rodar nova requisicao no endpoint de tasks.
export const REMOVE_OPPORTUNITIES_KANBAN = (state, payload) => {
  if (!payload || !state || !state.opportunitiesKanban) return
  const ids = (Array.isArray(payload) ? payload : [payload])
    .map((item) => (item && typeof item === 'object' ? item.id : item))
    .filter(Boolean)
    .map(String)
  if (!ids.length) return
  state.opportunitiesKanban.data = state.opportunitiesKanban.data.filter(
    (item) => !ids.includes(String(item.id))
  )
}
// Optimistic update do drag-end: muta a fase de um item no array sem
// disparar nova request. Caller eh responsavel pelo rollback em caso de erro.
export const UPDATE_OPPORTUNITY_KANBAN_FASE = (state, payload) => {
  if (!payload || !payload.id || !state || !state.opportunitiesKanban) return
  const item = state.opportunitiesKanban.data.find((i) => i.id === payload.id)
  if (item && item.metadata) {
    item.metadata.fase = payload.fase
  }
}

// Atualiza cliente.status_lead em todos os items do kanban que pertencem a um
// id_lead/id_cliente_app especifico. Usado quando o lead vira "convertido"
// (ganho) ou muda status — sem isso, resolveClienteTarget mantem decisao
// antiga e o proximo clique no card abre o modal errado.
export const PATCH_CLIENTE_IN_OPPORTUNITIES_KANBAN = (state, payload) => {
  if (!payload || !state || !state.opportunitiesKanban) return
  const { idClienteApp, clienteData } = payload
  if (!idClienteApp || !clienteData || typeof clienteData !== 'object') return

  const data = state.opportunitiesKanban.data
  if (!Array.isArray(data)) return

  // Card de oportunidade (kanban-card-opportunity.vue) lê `cliente.nome`,
  // mas no modal o campo se chama `nome_cliente` — mapeamos pra garantir
  // que a edição reflete no display. Só toca `nome` quando um dos dois
  // veio no payload — clienteData pode ser um patch parcial de 1 campo
  // (ex: editando só `id_origem`), e `nome: undefined` apagaria o nome
  // já exibido no card.
  const clientePatch = { ...clienteData }
  if (clienteData.nome_cliente !== undefined) {
    clientePatch.nome = clienteData.nome_cliente
  } else if (clienteData.nome !== undefined) {
    clientePatch.nome = clienteData.nome
  }

  data.forEach((item, idx) => {
    if (!item || !item.cliente) return
    if (Number(item.cliente.id_cliente_app) !== Number(idClienteApp)) return
    data.splice(idx, 1, {
      ...item,
      cliente: { ...item.cliente, ...clientePatch },
    })
  })
}

export const UPDATE_LEAD_STATUS_IN_OPPORTUNITIES_KANBAN = (state, payload) => {
  if (!payload || !payload.status || !state || !state.opportunitiesKanban) {
    return
  }
  const { id_lead, id_cliente_app, status } = payload
  const sameCliente = (cliente) => {
    if (id_lead && cliente.id_lead === id_lead) return true
    if (
      id_cliente_app &&
      cliente.id_cliente_app &&
      Number(cliente.id_cliente_app) === Number(id_cliente_app)
    ) {
      return true
    }
    return false
  }
  const data = state.opportunitiesKanban.data
  data.forEach((item, idx) => {
    if (!item || !item.cliente || !sameCliente(item.cliente)) return
    data.splice(idx, 1, {
      ...item,
      cliente: { ...item.cliente, status_lead: status },
    })
  })
}

export const SET_CALENDAR = (state, payload) => {
  if (state && state.calendar) {
    state.calendar.data = cloneArray(payload)
    state.calendar.lastUpdate = new Date()

    // Reseta o cache de meses carregados — os 3 meses iniciais serão
    // re-marcados como carregados pelo API layer (_markInitialMonthsAsLoaded).
    state.calendar.loadedMonths = {}
  }
}
export const ADD_CALENDAR = (state, payload) => {
  if (!Array.isArray(payload)) return

  const dias = JSON.parse(JSON.stringify(payload))

  dias.forEach((diaNew) => {
    const diaIndex = state.calendar.data.findIndex(
      (item) => item.data === diaNew.data
    )

    if (diaIndex === -1) {
      state.calendar.data.push(diaNew)
      return
    }

    const diaExistente = state.calendar.data[diaIndex]

    // Collect all client IDs from the new payload
    const clientIds = {}
    const newAtividades = diaNew.atividades || []
    newAtividades.forEach((a) => {
      const id = a.metadata && a.metadata.id_cliente_app
      if (id) clientIds[id] = true
    })

    // Remove all existing activities for those clients
    diaExistente.atividades = (diaExistente.atividades || []).filter((a) => {
      const id = a.metadata && a.metadata.id_cliente_app
      return !clientIds[id]
    })

    // Add all new activities from the payload
    newAtividades.forEach((atividadeNew) => {
      diaExistente.atividades.push(atividadeNew)
    })
  })
}
export const SET_CALENDAR_LOADING = (state, payload) => {
  if (state && state.calendar) {
    state.calendar.loading = Boolean(payload)
  }
}

export const SET_FILTER_CALENDAR = (state, payload) => {
  if (payload && typeof payload === 'object') {
    state.filterCalendar = payload
  }
}

export const SET_CALENDAR_GOOGLE_AGENDA = (state, payload) => {
  if (state && state.calendar) {
    state.calendar.googleAgenda = payload || { autenticado: false, erro: null }
  }
}

export const SET_CALENDAR_MICROSOFT_AGENDA = (state, payload) => {
  if (state && state.calendar) {
    state.calendar.microsoftAgenda = payload || {
      autenticado: false,
      erro: null,
    }
  }
}

/**
 * Substitui os eventos externos do `provedor` informado nos dias presentes em
 * `payload.days`. Eventos de outros provedores permanecem intactos — isso
 * permite reload pontual de Google sem zerar Microsoft e vice-versa.
 *
 * @param {Object} payload - { days: Array, provedor: string }
 */
export const UPDATE_CALENDAR_EXTERNAL_EVENTS = (state, payload) => {
  if (!payload || !Array.isArray(payload.days)) return
  const provedor = payload.provedor || 'google'
  const diasExternos = JSON.parse(JSON.stringify(payload.days))

  // Remove apenas os eventos do provedor informado (mantém os do outro provedor)
  state.calendar.data.forEach((dia) => {
    if (dia.atividades && Array.isArray(dia.atividades)) {
      dia.atividades = dia.atividades.filter((a) => {
        if (a.tipo !== 'provedor-externo') return true
        const metaProvedor = a.metadata && a.metadata.provedor
        return metaProvedor !== provedor
      })
    }
  })

  // Adiciona os novos eventos do provedor (filtrando para garantir consistência)
  diasExternos.forEach((diaNew) => {
    const atividadesProvedor = (diaNew.atividades || []).filter((a) => {
      if (a.tipo !== 'provedor-externo') return false
      const metaProvedor = a.metadata && a.metadata.provedor
      // Se o backend ainda não populou metadata.provedor, presume o pedido atual
      return !metaProvedor || metaProvedor === provedor
    })

    if (!atividadesProvedor.length) return

    const diaIndex = state.calendar.data.findIndex(
      (item) => item.data === diaNew.data
    )

    if (diaIndex === -1) {
      state.calendar.data.push({
        data: diaNew.data,
        atividades: atividadesProvedor,
      })
      return
    }

    const diaExistente = state.calendar.data[diaIndex]
    if (!Array.isArray(diaExistente.atividades)) {
      diaExistente.atividades = []
    }
    atividadesProvedor.forEach((atividadeNew) => {
      diaExistente.atividades.push(atividadeNew)
    })
  })

  sortCalendarDays(state.calendar.data)
}

// Alias mantido para callers existentes (DEPRECATED): considera os eventos como
// sendo do Google (comportamento original) — usar UPDATE_CALENDAR_EXTERNAL_EVENTS
// com `provedor` explícito em código novo.
export const UPDATE_CALENDAR_GOOGLE_EVENTS = (state, payload) => {
  UPDATE_CALENDAR_EXTERNAL_EVENTS(state, { days: payload, provedor: 'google' })
}

export const ADD_SINGLE_GOOGLE_EVENT = (state, payload) => {
  if (!payload || !payload.data) return

  const atividade = JSON.parse(JSON.stringify(payload))
  const dataDia = atividade.data

  if (!state || !state.calendar || !Array.isArray(state.calendar.data)) return

  const diaIndex = state.calendar.data.findIndex(
    (item) => item.data === dataDia
  )

  if (diaIndex === -1) {
    // Dia não existe, cria novo
    state.calendar.data.push({ data: dataDia, atividades: [atividade] })
    // Reordena os dias
    sortCalendarDays(state.calendar.data)
  } else {
    // Dia existe, adiciona a atividade
    if (!Array.isArray(state.calendar.data[diaIndex].atividades)) {
      state.calendar.data[diaIndex].atividades = []
    }
    state.calendar.data[diaIndex].atividades.push(atividade)
  }
}

/**
 * Faz merge dos dias de um mês específico no store.
 * Remove todos os dias do mês informado e insere os novos.
 * Dias de outros meses permanecem intactos.
 * @param {Object} payload - { monthKey: "YYYY-MM", dias: [...] }
 */
export const MERGE_CALENDAR_MONTH = (state, payload) => {
  if (!payload || !payload.monthKey || !Array.isArray(payload.dias)) return
  if (!state || !state.calendar || !Array.isArray(state.calendar.data)) return

  const monthKey = payload.monthKey // "YYYY-MM"
  const newDias = JSON.parse(JSON.stringify(payload.dias))

  // Remove todos os dias do mês que está sendo carregado
  state.calendar.data = state.calendar.data.filter((dia) => {
    if (!dia || !dia.data) return true
    const parts = dia.data.split('/')
    if (parts.length !== 3) return true
    const diaMonthKey = parts[2] + '-' + parts[1] // YYYY-MM
    return diaMonthKey !== monthKey
  })

  // Adiciona os novos dias
  newDias.forEach((dia) => {
    state.calendar.data.push(dia)
  })

  // Reordena os dias
  sortCalendarDays(state.calendar.data)
}

export const ADD_LOADED_MONTH = (state, monthKey) => {
  if (state && state.calendar && monthKey) {
    state.calendar.loadedMonths = {
      ...state.calendar.loadedMonths,
      [monthKey]: true,
    }
  }
}

export const SET_CALENDAR_LOADING_MONTH = (state, payload) => {
  if (state && state.calendar) {
    state.calendar.loadingMonth = Boolean(payload)
  }
}

/**
 * Remove um evento Google do store pelo id_evento_provedor.
 * @param {string} payload - id_evento_provedor do evento a remover
 */
export const REMOVE_GOOGLE_EVENT = (state, idEventoProvedor) => {
  if (!idEventoProvedor) return
  if (!state || !state.calendar || !Array.isArray(state.calendar.data)) return

  for (let i = 0; i < state.calendar.data.length; i++) {
    const dia = state.calendar.data[i]
    if (!dia.atividades || !Array.isArray(dia.atividades)) continue

    const antes = dia.atividades.length
    dia.atividades = dia.atividades.filter((atv) => {
      const meta = atv.metadata || {}
      return meta.id_evento_provedor !== idEventoProvedor
    })

    // Se o dia ficou sem atividades, remove o dia
    if (dia.atividades.length === 0 && antes > 0) {
      state.calendar.data.splice(i, 1)
      i--
    }
  }
}

/**
 * Atualiza um evento Google no store pelo id_evento_provedor.
 * Remove o evento antigo e adiciona o novo formatado.
 * @param {Object} payload - { id_evento_provedor: string, atividade: Object }
 */
export const UPDATE_GOOGLE_EVENT = (state, payload) => {
  if (!payload || !payload.id_evento_provedor || !payload.atividade) return
  if (!state || !state.calendar || !Array.isArray(state.calendar.data)) return

  REMOVE_GOOGLE_EVENT(state, payload.id_evento_provedor)
  ADD_SINGLE_GOOGLE_EVENT(state, payload.atividade)
}

export const SET_OBSERVACAO_CLIENT = (state, payload) => {
  if (!payload || !payload.idClienteApp) return
  if (!state || !state.observacoes) return

  Vue.set(state.observacoes.byClient, payload.idClienteApp, {
    observacao: payload.observacao || '',
    fetchedAt: new Date(),
  })
}

export const SET_OBSERVACAO_CLIENT_LOADING = (state, payload) => {
  if (!payload || !payload.idClienteApp) return
  if (!state || !state.observacoes) return

  Vue.set(
    state.observacoes.loadingByClient,
    payload.idClienteApp,
    Boolean(payload.loading)
  )
}

export const INVALIDATE_OBSERVACAO_CLIENT = (state, idClienteApp) => {
  if (!idClienteApp) return
  if (!state || !state.observacoes) return

  Vue.delete(state.observacoes.byClient, idClienteApp)
}

export const SET_CLIENTE_LEAD_STATUS = (state, payload) => {
  if (!payload || !payload.idClienteApp) return
  if (!state || !state.clienteLeadStatus) return

  Vue.set(state.clienteLeadStatus.byClienteApp, payload.idClienteApp, {
    id_lead: payload.id_lead === undefined ? null : payload.id_lead,
    status_lead: payload.status_lead === undefined ? null : payload.status_lead,
    fetchedAt: new Date(),
  })
}

export const SET_CLIENTE_LEAD_STATUS_LOADING = (state, payload) => {
  if (!payload || !payload.idClienteApp) return
  if (!state || !state.clienteLeadStatus) return

  Vue.set(
    state.clienteLeadStatus.loadingByClienteApp,
    payload.idClienteApp,
    Boolean(payload.loading)
  )
}

/**
 * Atualiza o horário de uma atividade CRM (lead/cliente) no store.
 * Atualiza TODAS as ocorrências com o mesmo id (atividades de relacionamento
 * geram duas entradas no calendário — concluída e pendente — com o mesmo id).
 * @param {Object} payload - { id: string, horario: string }
 */
export const UPDATE_ACTIVITY_TIME = (state, payload) => {
  if (!payload || !payload.id || !payload.horario) return
  if (!state || !state.calendar || !Array.isArray(state.calendar.data)) return

  const activities = findCalendarActivitiesByPayload(
    state.calendar.data,
    payload
  )
  for (const activity of activities) {
    Vue.set(activity, 'horario', payload.horario)
  }
}

/**
 * Move uma atividade de régua de relacionamento para o bucket do novo dia
 * após uma alteração de data (RELOAD.CRM_CALENDAR_DATE_UPDATED — atualização
 * otimista, sem recarregar o período inteiro). Atualiza TODAS as ocorrências
 * com o mesmo id (mesmo racional de UPDATE_ACTIVITY_TIME — atividades de
 * relacionamento geram duas entradas no calendário, concluída e pendente,
 * com o mesmo id). Cria o bucket do dia de destino quando ele ainda não
 * existe no período carregado.
 * @param {Object} payload - { id: string, idLead?: string, data: string (DD/MM/YYYY), horario?: string }
 */
export const UPDATE_ACTIVITY_DATE = (state, payload) => {
  if (!payload || !payload.id || !payload.data) return
  if (!state || !state.calendar || !Array.isArray(state.calendar.data)) return

  const days = state.calendar.data
  const activities = findCalendarActivitiesByPayload(days, payload)
  if (!activities.length) return

  activities.forEach((activity) => {
    const currentDay = days.find(
      (day) =>
        Array.isArray(day.atividades) && day.atividades.indexOf(activity) !== -1
    )
    if (currentDay) {
      currentDay.atividades.splice(currentDay.atividades.indexOf(activity), 1)
    }

    Vue.set(activity, 'data', payload.data)
    if (payload.horario) Vue.set(activity, 'horario', payload.horario)

    let targetDay = days.find((day) => day.data === payload.data)
    if (!targetDay) {
      targetDay = { data: payload.data, atividades: [] }
      days.push(targetDay)
    }
    if (!Array.isArray(targetDay.atividades)) targetDay.atividades = []
    targetDay.atividades.push(activity)
  })

  sortCalendarDays(days)
}

export const SET_CRM_EMAILS = (state, { scope, data }) => {
  const s = getEmailScope(state, scope)
  if (s) {
    s.data = Array.isArray(data) ? data : []
    s.lastUpdate = new Date()
  }
}

export const SET_CRM_EMAILS_LOADING = (state, { scope, loading }) => {
  const s = getEmailScope(state, scope)
  if (s) s.loading = Boolean(loading)
}

export const SET_CRM_EMAILS_CONTADORES = (state, { scope, contadores }) => {
  const s = getEmailScope(state, scope)
  if (s && contadores) s.contadores = contadores
}

export const SET_CRM_EMAILS_PAGINATION = (state, { scope, pagination }) => {
  const s = getEmailScope(state, scope)
  if (s && pagination) Object.assign(s.pagination, pagination)
}

export const SET_CRM_EMAILS_FOLDER = (state, { scope, folder }) => {
  const s = getEmailScope(state, scope)
  if (s) s.folder = folder
}

export const SET_CRM_EMAILS_CLIENT = (state, { idClienteApp }) => {
  const s = getEmailScope(state, 'cliente')
  if (s) s.idClienteApp = idClienteApp
}

export const ADD_CRM_EMAIL = (state, { scope, email }) => {
  const s = getEmailScope(state, scope)
  if (!s || !email) return
  const threadId = (email.metadata || {}).thread_id
  if (threadId) {
    const existing = s.data.find((m) => {
      const tid = (m.metadata || {}).thread_id
      return tid && tid === threadId
    })
    if (existing) {
      if (!existing.thread_emails) existing.thread_emails = []
      existing.thread_emails.unshift(email)
      existing.thread_count = (existing.thread_count || 1) + 1
      return
    }
  }
  s.data.unshift(email)
}

export const REMOVE_CRM_EMAIL = (state, { scope, id }) => {
  const s = getEmailScope(state, scope)
  if (s) s.data = s.data.filter((m) => m.id !== id)
}

export const REMOVE_CRM_EMAILS = (state, { scope, ids }) => {
  const s = getEmailScope(state, scope)
  if (s && Array.isArray(ids)) {
    const idSet = new Set(ids)
    s.data = s.data.filter((m) => !idSet.has(m.id))
  }
}

export const CLEAR_CRM_EMAILS_SCOPE = (state, { scope }) => {
  const s = getEmailScope(state, scope)
  if (!s) return
  s.data = []
  s.contadores = { todos: 0, recebido: 0, enviado: 0, lixeira: 0 }
  s.loading = false
  s.lastUpdate = null
  s.pagination = { page: 1, per_page: 20, total: 0, pages: 0 }
  s.folder = 'todos'
  if (scope === 'cliente') s.idClienteApp = null
}

export const SET_CRM_EMAILS_FIRST_AUTO_SYNC_DONE = (state, done) => {
  if (!state || !state.crmEmails) return
  state.crmEmails.firstAutoSyncDone = Boolean(done)
}

export const MARK_CRM_EMAIL_READ = (state, { scope, id, aberto }) => {
  const s = getEmailScope(state, scope)
  if (!s) return
  for (const msg of s.data) {
    if (msg.id === id) {
      msg.aberto = aberto
      // data_abertura NAO eh resetado — eh exclusivo do tracking pixel
      return
    }
    const thread = msg.thread_emails || []
    const threadMsg = thread.find((t) => t.id === id)
    if (threadMsg) {
      threadMsg.aberto = aberto
      return
    }
  }
}

export const MARK_CRM_EMAILS_READ = (state, { scope, ids, aberto }) => {
  const s = getEmailScope(state, scope)
  if (!s || !Array.isArray(ids)) return
  const idSet = new Set(ids)
  s.data.forEach((msg) => {
    if (idSet.has(msg.id)) {
      msg.aberto = aberto
      // data_abertura NAO eh resetado — eh exclusivo do tracking pixel
    }
    const thread = msg.thread_emails || []
    thread.forEach((t) => {
      if (idSet.has(t.id)) {
        t.aberto = aberto
      }
    })
  })
}

/**
 * Ajusta contadores otimisticamente sem chamar o backend.
 * Substitui o objeto inteiro para garantir reatividade no Vue 2.
 * @param {Object} deltas - ex: { totais: { todos: -1 }, nao_lidos: { recebido: -1 } }
 */
export const ADJUST_CRM_EMAILS_CONTADORES = (state, { scope, deltas }) => {
  const s = getEmailScope(state, scope)
  if (!s || !s.contadores || !deltas) return

  const updated = JSON.parse(JSON.stringify(s.contadores))
  for (const [group, groupDeltas] of Object.entries(deltas)) {
    if (!updated[group]) updated[group] = {}
    for (const [key, delta] of Object.entries(groupDeltas)) {
      updated[group][key] = Math.max(0, (updated[group][key] || 0) + delta)
    }
  }
  s.contadores = updated
}

export const SET_CUSTOM_FIELDS = (state, payload) => {
  const value = Array.isArray(payload) ? payload : []
  state.customFields.data = value
  state.customFields.lastUpdate = new Date()
  state.customFields.loaded = true
}
export const SET_CUSTOM_FIELDS_LOADING = (state, payload) => {
  state.customFields.loading = !!payload
}
export const RESET_CUSTOM_FIELDS = (state) => {
  state.customFields.data = []
  state.customFields.lastUpdate = null
  state.customFields.loaded = false
  state.customFields.loading = false
}

export const SET_FICHA_SCHEMA = (state, payload) => {
  state.fichaSchema.data =
    payload && typeof payload === 'object' ? payload : null
  state.fichaSchema.lastUpdate = new Date()
  state.fichaSchema.loaded = true
}
export const SET_FICHA_SCHEMA_LOADING = (state, payload) => {
  state.fichaSchema.loading = !!payload
}

export const SET_SUMMARY_LAYOUT = (state, { entityType, layout }) => {
  if (!entityType) return
  state.summary.layouts = {
    ...state.summary.layouts,
    [entityType]: Array.isArray(layout) ? layout : [],
  }
  state.summary.layoutsLoaded = {
    ...state.summary.layoutsLoaded,
    [entityType]: true,
  }
}

export const SET_SUMMARY_CATALOG = (state, { entityType, fields }) => {
  if (!entityType) return
  state.summary.catalogs = {
    ...state.summary.catalogs,
    [entityType]: Array.isArray(fields) ? fields : [],
  }
  state.summary.catalogsLoaded = {
    ...state.summary.catalogsLoaded,
    [entityType]: true,
  }
}

export const SET_SUMMARY_DATA = (
  state,
  { idClienteApp, entityType, layout, data, customizationBlocked }
) => {
  if (!idClienteApp) return
  state.summary.dataByClienteId = {
    ...state.summary.dataByClienteId,
    [idClienteApp]: {
      entityType,
      layout: Array.isArray(layout) ? layout : [],
      data: data && typeof data === 'object' ? data : {},
      customizationBlocked: Boolean(customizationBlocked),
      lastUpdate: new Date(),
    },
  }
}

export const PATCH_SUMMARY_FIELD = (
  state,
  { idClienteApp, fieldKey, value }
) => {
  if (!idClienteApp || !fieldKey) return
  const current = state.summary.dataByClienteId[idClienteApp]
  if (!current) return
  state.summary.dataByClienteId = {
    ...state.summary.dataByClienteId,
    [idClienteApp]: {
      ...current,
      data: { ...current.data, [fieldKey]: value },
      lastUpdate: new Date(),
    },
  }
}

export const CLEAR_SUMMARY_DATA = (state, { idClienteApp }) => {
  if (!idClienteApp) return
  const next = { ...state.summary.dataByClienteId }
  delete next[idClienteApp]
  state.summary.dataByClienteId = next
}

export const CLEAR_ALL_SUMMARY_DATA = (state) => {
  state.summary.dataByClienteId = {}
}

export const SET_OVERVIEW_LAYOUT = (
  state,
  { scope, layout, isDefault, customizationBlocked }
) => {
  if (!scope || !state.overview) return
  state.overview.layouts = {
    ...state.overview.layouts,
    [scope]: Array.isArray(layout) ? layout : [],
  }
  state.overview.layoutsLoaded = {
    ...state.overview.layoutsLoaded,
    [scope]: true,
  }
  state.overview.isDefault = {
    ...state.overview.isDefault,
    [scope]: Boolean(isDefault),
  }
  state.overview.customizationBlocked = {
    ...state.overview.customizationBlocked,
    [scope]: Boolean(customizationBlocked),
  }
}

export const SET_OVERVIEW_CATALOG = (state, { blocks }) => {
  if (!state.overview) return
  state.overview.catalog = Array.isArray(blocks) ? blocks : []
  state.overview.catalogLoaded = true
}

// Fluxo A: patch in-place do cliente nas 4 slices. Não realoca grupos.
export const PATCH_CLIENT_IN_RELATIONSHIP = (state, payload) => {
  if (!payload || typeof payload !== 'object') return
  const idClienteApp = payload.idClienteApp
  const patch = payload.patch
  if (!idClienteApp || !patch) return

  forEachClienteInState(state, idClienteApp, (cliente) => {
    applyPatchToCliente(cliente, patch)
    Object.keys(patch).forEach((key) => {
      if (patch[key] === undefined) return
      Vue.set(cliente, key, cliente[key])
    })
  })
}

// Fluxo B: move o cliente entre grupos conforme o response e recomputa o summary.
export const UPSERT_CLIENT_IN_RELATIONSHIP_FROM_RESPONSE = (state, payload) => {
  if (!state || !state.clients || !payload) return
  const idClienteApp = payload.idClienteApp
  const responseData = payload.responseData
  if (!idClienteApp || !responseData) return

  const freshGroups = Array.isArray(responseData.grupos)
    ? responseData.grupos
    : []
  const clientGroups = Array.isArray(state.clients.data)
    ? state.clients.data
    : []

  removeClienteFromAllClientGroups(clientGroups, idClienteApp)

  freshGroups.forEach((freshGrupo) => {
    if (!freshGrupo || !freshGrupo.identificador) return
    const clientes = Array.isArray(freshGrupo.clientes)
      ? freshGrupo.clientes
      : []
    const target = clientes.find((c) => c && c.id_cliente_app === idClienteApp)
    if (!target) return
    upsertClienteInClientGroup({
      clientGroups,
      identificador: freshGrupo.identificador,
      cliente: target,
    })
  })

  // Nova ref pra invalidar getters/computed.
  state.clients.data = [...clientGroups]

  // Summary recalculado local (r.value.totais vem filtrado p/ 1 cliente).
  state.clients.summaryData = {
    ...state.clients.summaryData,
    ...computeSummaryFromClientGroups(clientGroups),
  }
}

// Fluxo B: substitui as tarefas do cliente em clientsTasks.data.
export const UPSERT_CLIENT_TASKS_IN_RELATIONSHIP_FROM_RESPONSE = (
  state,
  payload
) => {
  if (!state || !state.clientsTasks || !payload) return
  const idClienteApp = payload.idClienteApp
  const responseData = payload.responseData
  if (!idClienteApp || !responseData) return

  const freshGroups = Array.isArray(responseData.grupos)
    ? responseData.grupos
    : []
  const taskGroups = Array.isArray(state.clientsTasks.data)
    ? state.clientsTasks.data
    : []

  mergeTaskGroupsForCliente({
    existingTaskGroups: taskGroups,
    freshTaskGroups: freshGroups,
    idClienteApp,
  })

  state.clientsTasks.data = [...taskGroups]
}

// Fluxo B: move o cliente entre etapas do kanban.
export const UPSERT_CLIENT_KANBAN_IN_RELATIONSHIP_FROM_RESPONSE = (
  state,
  payload
) => {
  if (!state || !state.relationshipKanban || !payload) return
  const idClienteApp = payload.idClienteApp
  const responseData = payload.responseData
  if (!idClienteApp || !responseData) return

  const freshEtapas = Array.isArray(responseData.etapas)
    ? responseData.etapas
    : []
  const existingEtapas = Array.isArray(state.relationshipKanban.data)
    ? state.relationshipKanban.data
    : []

  mergeKanbanForCliente({
    existingEtapas,
    freshEtapas,
    idClienteApp,
  })

  state.relationshipKanban.data = [...existingEtapas]
}

export const RECOMPUTE_RELATIONSHIP_SUMMARY = (state) => {
  if (!state || !state.clients) return
  const clientGroups = Array.isArray(state.clients.data)
    ? state.clients.data
    : []
  state.clients.summaryData = {
    ...state.clients.summaryData,
    ...computeSummaryFromClientGroups(clientGroups),
  }
}

// CRM > Transcrições > Projetos (spec-004/005). Lista de projetos do escritório
// usada na aba de configuração. Padrão { data, loading, lastUpdate }.
export const SET_TRANSCRIPT_PROJECTS = (state, payload) => {
  state.transcriptProjects.data = Array.isArray(payload) ? payload : []
  state.transcriptProjects.lastUpdate = new Date()
}
export const SET_TRANSCRIPT_PROJECTS_LOADING = (state, payload) => {
  state.transcriptProjects.loading = !!payload
}

// CRM > Transcrições > Avaliação: scores por critério de uma transcrição,
// indexados por id (Vue.set garante reatividade em chaves dinâmicas — Vue 2).
export const SET_TRANSCRIPT_SCORES = (state, { id, scores } = {}) => {
  if (!id) return
  Vue.set(
    state.transcriptScores.byTranscript,
    id,
    Array.isArray(scores) ? scores : []
  )
}
export const SET_TRANSCRIPT_SCORES_LOADING = (state, { id, loading } = {}) => {
  if (!id) return
  Vue.set(state.transcriptScores.loadingByTranscript, id, !!loading)
}

// RESET: restaura o state inicial (libera dados de cliente/escritório retidos
// entre trocas de perfil/escritório in-SPA). Reatribui cada chave a partir do
// factory — reusa a reatividade existente. [CU-86ajnmt4r]
export const RESET = makeResetMutation(buildInitialState)
